import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    clientSecret: String,
    paymentMode: String,
    successUrl: String,
    publishableKey: String,
    email: String,
    cartId: Number,
    shippingUrl: String,
    deliveryCountry: String
  }

  static targets = ["linkAuth", "addressElement", "paymentElement", "submit", "errors", "overlay", "stickyCta"]

  connect() {
    this.initializeElements()
    this.hideStickyCtaOnKeyboard()
  }

  disconnect() {
    if (this.viewportHandler && window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.viewportHandler)
    }
  }

  // iOS keeps fixed elements above the virtual keyboard — hide the sticky CTA when it opens
  hideStickyCtaOnKeyboard() {
    if (!window.visualViewport || !this.hasStickyCtaTarget) return

    const threshold = 150
    this.viewportHandler = () => {
      const keyboardOpen = window.innerHeight - window.visualViewport.height > threshold
      this.stickyCtaTarget.style.display = keyboardOpen ? "none" : ""
    }
    window.visualViewport.addEventListener("resize", this.viewportHandler)
  }

  initializeElements() {
    this.stripe = Stripe(this.publishableKeyValue)
    var isMobile = window.matchMedia && window.matchMedia("(max-width: 640px)").matches
    var inputSize = isMobile ? "16px" : "14px"
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecretValue,
      fonts: [{ cssSrc: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500&display=swap" }],
      appearance: {
        theme: "flat",
        variables: {
          fontFamily: "'DM Sans', sans-serif",
          fontSizeBase: inputSize,
          fontSizeSm: "12px",
          colorPrimary: "#1A1410",
          colorText: "#1A1410",
          colorTextSecondary: "#6B5E52",
          colorTextPlaceholder: "#B8A99A",
          colorBackground: "#FFFFFF",
          borderRadius: "8px",
          spacingUnit: "4px",
          spacingGridRow: "14px",
          spacingGridColumn: "14px"
        },
        rules: {
          ".Input": {
            border: "1.5px solid #E0D5C8",
            padding: "10px 14px",
            fontSize: inputSize,
            transition: "border-color 0.2s"
          },
          ".Input:focus": {
            border: "1.5px solid #1A1410",
            boxShadow: "none"
          },
          ".Label": {
            fontSize: "12px",
            fontWeight: "500",
            color: "#6B5E52"
          },
          ".CheckboxInput": {
            border: "1.5px solid #E0D5C8",
            borderRadius: "4px"
          }
        }
      },
      locale: "fr"
    })

    // Email + Link authentication
    const linkAuthElement = this.elements.create("linkAuthentication", {
      defaultValues: { email: this.emailValue || "" }
    })
    linkAuthElement.mount(this.linkAuthTarget)

    // Shipping address
    const country = this.deliveryCountryValue || "FR"
    const addressElement = this.elements.create("address", {
      mode: "shipping",
      autocomplete: { mode: "automatic" },
      fields: { phone: "always" },
      validation: { phone: { required: "always" } },
      display: { name: "split" },
      defaultValues: {
        address: { country }
      },
      allowedCountries: [country]
    })
    addressElement.mount(this.addressElementTarget)

    // Payment — hide full billing address (only collect what's strictly required per method)
    const paymentElement = this.elements.create("payment", {
      terms: { card: "never" },
      fields: {
        billingDetails: { address: "if_required" }
      }
    })
    paymentElement.mount(this.paymentElementTarget)
  }

  async submit(event) {
    event.preventDefault()
    this.errorsTarget.textContent = ""
    this.submitTarget.disabled = true

    const { error: submitError } = await this.elements.submit()
    if (submitError) {
      this.showError(submitError.message)
      return
    }

    // Save shipping address to cart before confirming with Stripe
    const addressElement = this.elements.getElement("address")
    const { complete, value: addressValue } = await addressElement.getValue()
    if (complete) {
      const saved = await this.saveShippingAddress(addressValue)
      // 409: the cart was paid meanwhile (another tab). Confirming here would charge it twice.
      if (saved.status === 409) {
        window.location.href = this.successUrlValue
        return
      }
    }

    const confirmParams = { return_url: this.successUrlValue }

    this.showOverlay()
    const result = await this.stripe.confirmPayment({ elements: this.elements, confirmParams })

    if (result.error) {
      this.hideOverlay()
      this.showError(result.error.message)
    }
  }

  async saveShippingAddress(addressValue) {
    const meta = document.querySelector("meta[name='csrf-token']")
    const csrfToken = meta && meta.content

    return fetch(this.shippingUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({
        cart_id: this.cartIdValue,
        name: addressValue.name,
        address: addressValue.address,
        mobile_phone: addressValue.phone
      })
    })
  }

  showOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("visible")
  }

  hideOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.remove("visible")
  }

  showError(message) {
    this.errorsTarget.textContent = message
    this.submitTarget.disabled = false
  }
}
